import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField
} from '@mui/material';
import React, { useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { CreateCostGroupRequest, ProductCategoryDto, ProductGroupDto } from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { createCostGroup, hideNewCostGroupDialog } from '../redux/calculationSlices';
import ConditionSelector from './ConditionSelector';

const mapStateToProps = (state: RootState) => ({
    calculation: state.calculation.calculation,
    isCreateCostGroupDialogOpen: state.calculation.showHideCostGroupDialog
});

type Props = ReturnType<typeof mapStateToProps>;

const CreateCostGroupDialog = ({ calculation, isCreateCostGroupDialogOpen }: Props) => {
    const dispatch = useDispatch();
    const dispatchHideNewCostGroupDialog = () => {
        dispatch(hideNewCostGroupDialog());
    };

    const getFormInitialState = () => ({
        name: '',
        description: '',
        useBasePrice: false,
        groupType: 0,
        productGroups: [] as number[],
        productCategories: [] as number[],
        productCategories2: [] as number[]
    });

    const [formState, setFormState] = useState(getFormInitialState());

    const handleNameChange = (inputValue: string) => {
        setFormState({
            ...formState,
            name: inputValue
        });
    };

    const handleDescriptionChange = (inputValue: string) => {
        setFormState({
            ...formState,
            description: inputValue
        });
    };
    const handleUseBasePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({
            ...formState,
            useBasePrice: event.target.checked
        });
    };
    const handleProductGroupChanged = (event: React.ChangeEvent<unknown>, child: ProductGroupDto[]) => {
        setFormState({
            ...formState,
            productGroups: child.map(c => c.productGroupId) ?? []
        });
    };

    const handleProductCategoriesChanged = (
        event: React.ChangeEvent<unknown>,
        value: ProductCategoryDto[]
    ) => {
        setFormState({
            ...formState,
            productCategories: value.map(c => c.productCategoryId) ?? []
        });
    };
    const handleProductCategories2Changed = (
        event: React.ChangeEvent<unknown>,
        value: ProductCategoryDto[]
    ) => {
        setFormState({ ...formState, productCategories2: value.map(c => c.productCategoryId) ?? [] });
    };

    const handleGroupTypeChange = (event: SelectChangeEvent<number>) => {
        setFormState({ ...formState, groupType: Number(event.target.value) });
    };
    const dispatchCreateNewCostGroup = () => {
        const costGroupDto: CreateCostGroupRequest = {
            calculationId: calculation.calculationId,
            name: formState.name,
            description: formState.description,
            useBasePrice: formState.useBasePrice,
            order: calculation.costGroups != null ? calculation.costGroups?.length + 1 : 0,
            overridesCostGroupId: undefined,
            costGroupType: formState.groupType,
            appliesToProductGroupIds: formState.productGroups ?? [],
            appliesToProductCategoryIds: formState.productCategories ?? [],
            appliesToProductCategory2Ids: formState.productCategories2 ?? []
        };

        dispatch(
            createCostGroup.request({
                pricelistId: calculation.pricelistId,
                costGroupDto: costGroupDto
            })
        );
        setFormState(getFormInitialState());
    };
    return (
        <Dialog
            onClose={dispatchHideNewCostGroupDialog}
            aria-labelledby="simple-dialog-title"
            open={isCreateCostGroupDialogOpen}
        >
            <DialogTitle id="simple-dialog-title">Create cost group</DialogTitle>
            <DialogContent>
                <ConditionSelector
                    costGroupProductCategories={formState?.productCategories ?? []}
                    costGroupProductGroups={formState?.productGroups ?? []}
                    costGroupProductCategories2={formState?.productCategories2}
                    handleProductCategoryChanged={handleProductCategoriesChanged}
                    handleProductGroupChanged={handleProductGroupChanged}
                    handleProductCategory2Changed={handleProductCategories2Changed}
                />
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="Name"
                    type="text"
                    fullWidth={true}
                    onChange={e => handleNameChange(e.target.value)}
                />
                <TextField
                    margin="dense"
                    id="description"
                    label="Description"
                    type="text"
                    fullWidth={true}
                    onChange={e => handleDescriptionChange(e.target.value)}
                />
                <Select<number> onChange={handleGroupTypeChange} defaultValue={0}>
                    <MenuItem value={0}>Stand alone calculation</MenuItem>
                    <MenuItem value={1}>Grouped percentages</MenuItem>
                </Select>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formState.useBasePrice}
                            onChange={e => handleUseBasePrice(e)}
                            name="useBasePrice"
                            color="primary"
                        />
                    }
                    label="Calculate using base price"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={dispatchHideNewCostGroupDialog} color="primary">
                    Close
                </Button>
                <Button onClick={dispatchCreateNewCostGroup} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(CreateCostGroupDialog);
